import { useEffect, useRef, useState } from 'react';
import { MessageSquare, Send } from 'lucide-react';

export interface NoteEntry {
    id: string;
    author: string;
    at: string;
    text: string;
}

const STORAGE_KEY_PREFIX = 'ats-spa.report-notes.';

function relative(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(ms)) return '';
    const sec = Math.round(ms / 1000);
    if (sec < 60) return 'just now';
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.round(hr / 24);
    return `${days}d ago`;
}

function loadNotes(applicationId: string): NoteEntry[] {
    if (typeof window === 'undefined') return [];
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY_PREFIX + applicationId);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? (parsed as NoteEntry[]) : [];
    } catch {
        return [];
    }
}

function saveNotes(applicationId: string, notes: NoteEntry[]) {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(STORAGE_KEY_PREFIX + applicationId, JSON.stringify(notes));
    } catch {
        /* ignore quota errors */
    }
}

export function NotesTab({
    applicationId,
    currentUserName,
}: {
    applicationId: string;
    currentUserName: string;
}) {
    const [notes, setNotes] = useState<NoteEntry[]>(() => loadNotes(applicationId));
    const [draft, setDraft] = useState('');
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        setNotes(loadNotes(applicationId));
    }, [applicationId]);

    const submit = () => {
        const trimmed = draft.trim();
        if (!trimmed) return;
        const entry: NoteEntry = {
            id: `n-${Date.now()}`,
            author: currentUserName,
            at: new Date().toISOString(),
            text: trimmed,
        };
        const next = [...notes, entry];
        setNotes(next);
        saveNotes(applicationId, next);
        setDraft('');
        inputRef.current?.focus();
    };

    return (
        <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto px-5 pt-5 pb-3 flex flex-col gap-3.5">
                {notes.length === 0 && (
                    <p
                        className="text-[13px] text-center py-8"
                        style={{ color: 'var(--text-tertiary)' }}
                    >
                        <MessageSquare
                            size={20}
                            strokeWidth={1.5}
                            style={{ display: 'inline', marginBottom: 8, color: 'var(--text-quaternary)' }}
                        />
                        <br />
                        No notes yet. Drop the first one below — it's saved locally per candidate.
                    </p>
                )}
                {notes.map((n) => (
                    <div key={n.id} className="flex items-start gap-3">
                        <div
                            className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-[11px] font-semibold text-white mt-0.5"
                            style={{ background: 'var(--accent)' }}
                        >
                            {n.author
                                .split(/\s+/)
                                .map((p) => p[0]?.toUpperCase() ?? '')
                                .slice(0, 2)
                                .join('')}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                                <span
                                    className="text-[14px] font-semibold"
                                    style={{ color: 'var(--text-primary)' }}
                                >
                                    {n.author}
                                </span>
                                <span
                                    className="text-[12px]"
                                    style={{ color: 'var(--text-quaternary)' }}
                                >
                                    {relative(n.at)}
                                </span>
                            </div>
                            <p
                                className="text-[14px] mt-1 whitespace-pre-wrap leading-relaxed"
                                style={{ color: 'var(--text-secondary)' }}
                            >
                                {n.text}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
            <div
                className="px-5 py-3 flex items-center gap-2"
                style={{
                    borderTop: '1px solid var(--border-muted)',
                    background: 'var(--surface-secondary)',
                }}
            >
                <input
                    ref={inputRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            submit();
                        }
                    }}
                    placeholder="Add a note (autosaves locally)…"
                    className="flex-1 h-10 px-4 rounded-full outline-none text-[13.5px]"
                    style={{
                        background: 'var(--surface-primary)',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--border-default)',
                    }}
                />
                <button
                    onClick={submit}
                    disabled={!draft.trim()}
                    className="w-10 h-10 flex items-center justify-center rounded-full disabled:opacity-40 transition"
                    style={{ background: 'var(--accent)' }}
                    aria-label="Add note"
                    type="button"
                >
                    <Send size={14} strokeWidth={2} color="white" />
                </button>
            </div>
        </div>
    );
}
